import { useEffect, useState } from "react";
import { BellRing, Archive, RotateCcw, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const ACTIONS = {
  keep: { title: "Mantieni sospeso", hint: "La sospensione resta invariata; imposta la prossima data di verifica.", color: "bg-slate-800 text-white" },
  reactivate: { title: "Riattiva l'azienda", hint: "Gli utenti tornano ad accedere a tutti i dati conservati. Richiede conferma esplicita.", color: "bg-[#2F7D32] text-white" },
  archive_review: { title: "Avvia valutazione archiviazione", hint: "Apre una verifica amministrativa e GDPR. Nessun dato, account o configurazione viene eliminato.", color: "bg-amber-500 text-slate-900" },
};

function DecisionDialog({ rem, action, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [confirm, setConfirm] = useState("");
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  if (!rem || !action) return null;
  const a = ACTIONS[action];
  const disabled = busy || (action === "archive_review" && !note.trim()) || (action === "reactivate" && confirm !== rem.tenant_id);
  const submit = async () => {
    setBusy(true);
    try { const r = await api.post(`/superadmin/suspension-reminders/${rem.id}/decision`, { action, note, confirm, next_review_days: Number(days) || 30 }); toast.success(r.data.message); onDone(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Operazione non riuscita"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="reminder-decision-dialog">
        <DialogHeader><DialogTitle>{a.title} — {rem.tenant_name}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-slate-600">{a.hint}</p>
          {action === "keep" && <div className="space-y-1"><Label>Prossima verifica tra (giorni)</Label><Input type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} data-testid="reminder-keep-days" /></div>}
          {action === "reactivate" && <div className="space-y-1"><Label>Digita l'identificativo <b>{rem.tenant_id}</b> per confermare</Label><Input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reminder-reactivate-confirm" /></div>}
          <div className="space-y-1"><Label>{action === "archive_review" ? "Motivazione (obbligatoria)" : "Nota (facoltativa)"}</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="reminder-note" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reminder-cancel">Annulla</Button>
          <Button disabled={disabled} onClick={submit} className={a.color} data-testid="reminder-submit">{a.title}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SuspensionReminders({ onChanged }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const load = () => api.get("/superadmin/suspension-reminders").then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  useEffect(() => { load(); }, []);
  const done = () => { load(); onChanged && onChanged(); };
  if (!data || !data.items.length) return null;
  return (
    <div className="space-y-2" data-testid="suspension-reminders">
      {data.items.map((r) => (
        <div key={r.id} className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-2" data-testid={`reminder-${r.tenant_id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <BellRing className="h-4 w-4 text-amber-600" />
            <span className="font-semibold text-slate-900">Promemoria sospensione · {r.tenant_name}</span>
            <span className="text-xs text-amber-800 rounded-full bg-amber-100 px-2 py-0.5" data-testid={`reminder-days-${r.tenant_id}`}>{r.days_suspended} giorni</span>
            {r.archive_review && <span className="text-xs text-slate-700 rounded-full bg-slate-200 px-2 py-0.5">in valutazione archiviazione</span>}
            {data.simulate && <span className="ml-auto text-[11px] text-slate-500">email in modalità simulata</span>}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-1 text-xs text-slate-700">
            <span>Sospesa dal: <b>{(r.suspended_at || "").slice(0, 10)}</b></span>
            <span>Modalità: <b>{r.mode || "—"}</b></span>
            <span className="lg:col-span-2">Motivo: <b>{r.reason || "—"}</b></span>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setSel({ rem: r, action: "keep" })} data-testid={`reminder-keep-${r.tenant_id}`}><Clock className="h-3.5 w-3.5 mr-1" />Mantieni sospeso</Button>
            <Button size="sm" className="h-7 text-xs bg-[#2F7D32] text-white" onClick={() => setSel({ rem: r, action: "reactivate" })} data-testid={`reminder-reactivate-${r.tenant_id}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Riattiva</Button>
            <Button size="sm" className="h-7 text-xs bg-amber-500 text-slate-900" onClick={() => setSel({ rem: r, action: "archive_review" })} data-testid={`reminder-archive-${r.tenant_id}`}><Archive className="h-3.5 w-3.5 mr-1" />Avvia valutazione archiviazione</Button>
          </div>
        </div>
      ))}
      {sel && <DecisionDialog rem={sel.rem} action={sel.action} onClose={() => setSel(null)} onDone={done} />}
    </div>
  );
}
